import {
  ELEMENTS,
  MOVES,
  effectiveness,
  formName,
  movesFor,
  speciesOf,
  type ClassId,
  type ElementId,
  type MoveDef,
  type OwnedLippie,
} from "./axolotl_lippies_data";

// Battle rules as plain functions. They change the battle state right away and push BEvents describing what
// happened; the game plays those events back one at a time so dice, damage and HP bars animate in order.

export type Rng = () => number;
export type Side = "you" | "foe";

export const LEVEL_CAP = 12;

export function roll(n: number, sides: number, rng: Rng) {
  return Array.from({ length: n }, () => 1 + Math.floor(rng() * sides));
}

const sum = (xs: number[]) => xs.reduce((a, b) => a + b, 0);

// ---------- stats ----------

const CLASS_STATS: Record<ClassId, { hp: number; hpPer: number; power: number; guard: number; speed: number }> = {
  fighter: { hp: 8, hpPer: 4.2, power: 1, guard: 12, speed: 3 },
  cleric: { hp: 8, hpPer: 4, power: 0, guard: 12, speed: 3 },
  ranger: { hp: 7, hpPer: 3.8, power: 0, guard: 11, speed: 5 },
  bard: { hp: 7, hpPer: 3.7, power: 0, guard: 11, speed: 5 },
  rogue: { hp: 6, hpPer: 3.6, power: 0, guard: 11, speed: 8 },
  wizard: { hp: 6, hpPer: 3.4, power: 1, guard: 10, speed: 4 },
};

// Wild Lippies are a little less sturdy than trained ones; the guardian is much sturdier.
export const WILD_HP = 0.8;
export const BOSS_HP = 2.4;

export function statsFor(form: string, level: number, hpScale = 1) {
  const c = CLASS_STATS[speciesOf(form).cls];
  return {
    maxHp: Math.round((c.hp + c.hpPer * level) * hpScale),
    power: c.power + Math.floor(level / 2),
    guard: c.guard + Math.floor(level / 4),
    speed: c.speed + level * 0.2,
  };
}

export const attackBonus = (level: number) => 2 + Math.floor(level / 2);
export const xpToNext = (level: number) => 10 + level * 8;
export const winXp = (foeLevel: number, trainer: boolean) => Math.round((4 + foeLevel * 3) * (trainer ? 1.5 : 1));

// ---------- fighters ----------

export interface Fighter {
  side: Side;
  form: string;
  name: string;
  element: ElementId;
  level: number;
  hp: number;
  shown: number;
  maxHp: number;
  power: number;
  guard: number;
  speed: number;
  moves: string[];
  uses: Record<string, number>;
  sleepy: number;
  slowed: number;
  marked: boolean;
  inspired: boolean;
  spicy: boolean;
  // Index into the party for the player's side.
  slot: number;
}

export function makeFighter(side: Side, form: string, level: number, opts: { hp?: number; knight?: boolean; hpScale?: number; nickname?: string; slot?: number } = {}): Fighter {
  const st = statsFor(form, level, opts.hpScale);
  const moves = movesFor(form);
  const hp = Math.min(st.maxHp, opts.hp ?? st.maxHp);
  return {
    side,
    form,
    name: opts.nickname || formName(form),
    element: speciesOf(form).element,
    level,
    hp,
    shown: hp,
    maxHp: st.maxHp,
    power: st.power,
    guard: st.guard + (opts.knight ? 1 : 0),
    speed: st.speed,
    moves,
    uses: Object.fromEntries(moves.filter((m) => MOVES[m].uses).map((m) => [m, MOVES[m].uses as number])),
    sleepy: 0,
    slowed: 0,
    marked: false,
    inspired: false,
    spicy: false,
    slot: opts.slot ?? -1,
  };
}

export function fighterFromOwned(l: OwnedLippie, slot: number, knight: boolean) {
  return makeFighter("you", l.form, l.level, { hp: l.hp, knight, nickname: l.nickname, slot });
}

export function moveElement(m: MoveDef, user: Fighter): ElementId | null {
  return m.element === "self" ? user.element : m.element;
}

// ---------- events ----------

export type BEvent =
  | { k: "text"; s: string }
  | { k: "roll"; who: Side; nat: number; other?: number; mod: number; vs: number; hit: boolean; label?: string }
  | { k: "dice"; who: Side; rolls: number[]; sides: number; bonus: number }
  | { k: "hit"; target: Side; hp: number; amount: number; mult: number; crit: boolean }
  | { k: "heal"; target: Side; hp: number; amount: number }
  | { k: "lunge"; who: Side }
  | { k: "sleep"; target: Side }
  | { k: "faint"; target: Side }
  | { k: "swap"; side: Side; fighter: Fighter }
  | { k: "seen"; key: string }
  | { k: "jar"; wobbles: number; caught: boolean }
  | { k: "sfx"; name: "levelUp" | "evolve" | "run" };

export type Action =
  | { k: "move"; id: string }
  | { k: "snack"; item: string }
  | { k: "swap"; slot: number; fighter: Fighter }
  | { k: "jar"; name: string; bonus: number; dc: number }
  | { k: "run" };

export interface Battle {
  kind: "wild" | "trainer" | "guardian";
  you: Fighter;
  foe: Fighter;
  ev: BEvent[];
  // Set once the battle is decided: the game shows results, then returns to the world.
  result: null | "won" | "lost" | "ran" | "caught";
  turn: number;
  inspireUsed: boolean;
}

export function newBattle(kind: Battle["kind"], you: Fighter, foe: Fighter): Battle {
  return { kind, you, foe, ev: [], result: null, turn: 0, inspireUsed: false };
}

const say = (b: Battle, s: string) => b.ev.push({ k: "text", s });

function other(b: Battle, side: Side) {
  return side === "you" ? b.foe : b.you;
}

function heal(b: Battle, f: Fighter, amount: number) {
  const gained = Math.min(amount, f.maxHp - f.hp);
  f.hp += gained;
  b.ev.push({ k: "heal", target: f.side, hp: f.hp, amount: gained });
  return gained;
}

const FUMBLES = [
  "{n} slipped on a lily pad!",
  "{n} sneezed at the worst moment!",
  "{n} tripped over its own tail!",
  "{n} got distracted by a butterfly!",
];

// One fighter uses one move. `first` is true when it acted before the other fighter this turn.
export function useMove(b: Battle, user: Fighter, moveId: string, first: boolean, rng: Rng) {
  const target = other(b, user.side);
  const m = MOVES[moveId];
  if (user.sleepy > 0) {
    user.sleepy--;
    say(b, user.sleepy > 0 ? `${user.name} is fast asleep... zzz` : `${user.name} is dozing... and wakes up!`);
    return;
  }
  if (m.uses) user.uses[moveId] = Math.max(0, (user.uses[moveId] ?? 0) - 1);
  say(b, `${user.name} used ${m.name}!`);

  if (m.kind === "heal") {
    const dice = roll(m.dice[0], m.dice[1], rng);
    const bonus = moveId === "second_wind" ? user.level : user.power;
    b.ev.push({ k: "dice", who: user.side, rolls: dice, sides: m.dice[1], bonus });
    const got = heal(b, user, sum(dice) + bonus);
    if (m.effect === "cure" && (user.sleepy > 0 || user.slowed > 0)) {
      user.sleepy = 0;
      user.slowed = 0;
    }
    say(b, got > 0 ? `${user.name} healed ${got} HP!` : `${user.name} is already feeling great!`);
    return;
  }

  b.ev.push({ k: "lunge", who: user.side });
  const a = 1 + Math.floor(rng() * 20);
  const second = user.inspired ? 1 + Math.floor(rng() * 20) : undefined;
  user.inspired = false;
  const nat = second !== undefined ? Math.max(a, second) : a;
  const mod = attackBonus(user.level) + m.acc + (target.marked && user.side !== target.side ? 2 : 0);
  const crit = nat === 20 || (nat >= 18 && speciesOf(user.form).cls === "rogue");
  const hit = nat !== 1 && (crit || nat + mod >= target.guard);
  b.ev.push({ k: "roll", who: user.side, nat, other: second !== undefined ? Math.min(a, second) : undefined, mod, vs: target.guard, hit });
  if (nat === 1) {
    say(b, FUMBLES[Math.floor(rng() * FUMBLES.length)].replace("{n}", user.name));
    return;
  }
  if (!hit) {
    say(b, `${user.name} missed!`);
    return;
  }

  if (m.kind === "lullaby") {
    if (target.sleepy > 0) {
      say(b, `${target.name} is already asleep.`);
      return;
    }
    target.sleepy = 1 + Math.floor(rng() * 2);
    b.ev.push({ k: "sleep", target: target.side });
    say(b, `${target.name} fell asleep!`);
    return;
  }

  const el = moveElement(m, user);
  const mult = el ? effectiveness(el, target.element) : 1;
  if (el) b.ev.push({ k: "seen", key: `${el}>${target.element}` });
  let rolls = roll(m.dice[0], m.dice[1], rng);
  if (crit) rolls = rolls.map(() => m.dice[1]).concat(roll(m.dice[0], m.dice[1], rng));
  if (m.effect === "sneak" && first) rolls = rolls.concat(roll(2, 6, rng));
  if (target.marked) rolls = rolls.concat(roll(1, 4, rng));
  if (user.spicy) {
    rolls = rolls.concat(roll(1, 6, rng));
    user.spicy = false;
  }
  b.ev.push({ k: "dice", who: user.side, rolls, sides: m.dice[1], bonus: user.power });
  const dmg = mult === 0 ? 0 : Math.max(1, Math.floor((sum(rolls) + user.power) * mult));
  target.hp = Math.max(0, target.hp - dmg);
  b.ev.push({ k: "hit", target: target.side, hp: target.hp, amount: dmg, mult, crit });
  if (crit) say(b, "Critical hit!");
  if (mult === 0) say(b, el === "thunder" ? "Boing! Rubber doesn't conduct electricity. No effect!" : "It had no effect!");
  else if (mult > 1) say(b, ELEMENTS[target.element].seaFruit && el === "water" ? "Super effective! It can't swim!" : "Super effective!");
  else if (mult < 1) say(b, "Not very effective...");
  if (m.effect === "sneak" && first) say(b, "Sneak attack bonus!");
  if (target.hp > 0) {
    if (m.effect === "drain") {
      const got = heal(b, user, 2);
      if (got) say(b, `${user.name} soaked up ${got} HP.`);
    }
    if (m.effect === "slow" && mult > 0) {
      target.slowed = 2;
      say(b, `${target.name} is slowed down!`);
    }
    if (m.effect === "mark" && !target.marked) {
      target.marked = true;
      say(b, `${target.name} is marked!`);
    }
  }
  if (target.hp === 0) faint(b, target);
}

function faint(b: Battle, f: Fighter) {
  b.ev.push({ k: "faint", target: f.side });
  say(b, `${f.name} is too sleepy to keep battling!`);
  if (f.side === "foe") b.result = "won";
}

// ---------- enemy choices ----------

// Wild Lippies mostly pick at random; trainers lean on type matchups.
export function chooseFoeMove(b: Battle, rng: Rng) {
  const smart = b.kind === "trainer";
  const me = b.foe;
  const them = b.you;
  const options = me.moves.filter((id) => !MOVES[id].uses || (me.uses[id] ?? 0) > 0);
  const weights = options.map((id) => {
    const m = MOVES[id];
    if (m.kind === "heal") return me.hp < me.maxHp * 0.35 ? 5 : me.hp < me.maxHp ? 0.15 : 0;
    if (m.kind === "lullaby") return them.sleepy > 0 ? 0 : 1.2;
    const el = moveElement(m, me);
    const mult = el ? effectiveness(el, them.element) : 1;
    if (mult === 0) return 0;
    let w = mult > 1 ? (smart ? 3 : 1.3) : mult < 1 ? 0.5 : 1;
    if (m.effect === "mark") w *= them.marked ? 0.5 : 1.4;
    if (id === "arcane_blast") w *= 1.5;
    if (id === "tackle") w *= 0.7;
    return w;
  });
  const total = sum(weights);
  if (total <= 0) return "tackle";
  let pick = rng() * total;
  for (let i = 0; i < options.length; i++) {
    pick -= weights[i];
    if (pick <= 0) return options[i];
  }
  return options[options.length - 1];
}

// ---------- turns ----------

function goesFirst(a: Fighter, c: Fighter, rng: Rng) {
  const sa = a.speed - (a.slowed > 0 ? 100 : 0);
  const sc = c.speed - (c.slowed > 0 ? 100 : 0);
  if (sa !== sc) return sa > sc;
  return rng() < 0.5;
}

function endTurn(b: Battle) {
  for (const f of [b.you, b.foe]) if (f.slowed > 0) f.slowed--;
  b.turn++;
}

// Resolve one full turn: the player's action plus the foe's reply.
export function playTurn(b: Battle, action: Action, rng: Rng, snackHeal: (item: string) => number) {
  const foeMove = chooseFoeMove(b, rng);
  const foeActs = () => {
    if (b.foe.hp > 0 && b.you.hp > 0) useMove(b, b.foe, foeMove, false, rng);
  };

  if (action.k === "run") {
    b.ev.push({ k: "sfx", name: "run" });
    say(b, "You got away safely!");
    b.result = "ran";
    return;
  }
  if (action.k === "jar") {
    const foe = b.foe;
    say(b, `You threw a ${action.name}!`);
    const nat = 1 + Math.floor(rng() * 20);
    // Tired or sleepy Lippies are easier to catch.
    const mod = action.bonus + Math.floor(10 * (1 - foe.hp / foe.maxHp)) + (foe.sleepy > 0 ? 3 : 0);
    const caught = nat === 20 || (nat !== 1 && nat + mod >= action.dc);
    const margin = nat + mod - action.dc;
    b.ev.push({ k: "roll", who: "you", nat, mod, vs: action.dc, hit: caught, label: "Catch" });
    b.ev.push({ k: "jar", wobbles: caught ? 3 : margin >= -3 ? 2 : 1, caught });
    if (caught) {
      say(b, `Gotcha! ${foe.name} was caught!`);
      b.result = "caught";
      return;
    }
    say(b, margin >= -3 ? `Argh! So close! ${foe.name} burst out of the jar!` : `Oh no! ${foe.name} burst out of the jar!`);
    foeActs();
    endTurn(b);
    return;
  }
  if (action.k === "swap") {
    say(b, `Come back, ${b.you.name}!`);
    b.you = action.fighter;
    b.ev.push({ k: "swap", side: "you", fighter: b.you });
    say(b, `Go, ${b.you.name}!`);
    foeActs();
  } else if (action.k === "snack") {
    const you = b.you;
    if (action.item === "pepper") {
      you.spicy = true;
      say(b, `${you.name} munched a Pepper. Spicy! Its next attack will hit harder.`);
    } else {
      const got = heal(b, you, snackHeal(action.item));
      say(b, `${you.name} ate the ${action.item === "cone" ? "Golden Cone" : "Cake"} and healed ${got} HP!`);
    }
    foeActs();
  } else {
    const youFirst = goesFirst(b.you, b.foe, rng);
    if (youFirst) {
      useMove(b, b.you, action.id, true, rng);
      foeActs();
    } else {
      useMove(b, b.foe, foeMove, true, rng);
      if (b.you.hp > 0) useMove(b, b.you, action.id, false, rng);
    }
  }
  endTurn(b);
}

// Only the foe acts (used after the player's Lippie is swapped in for a sleepy one).
export function foeOnlyTurn(b: Battle, rng: Rng) {
  useMove(b, b.foe, chooseFoeMove(b, rng), false, rng);
  endTurn(b);
}

// ---------- after battle ----------

export interface Growth {
  lines: string[];
  leveled: boolean;
  evolvedTo: string | null;
}

// Adds XP to an owned Lippie, handling level-ups and evolution. Returns what to tell the player.
export function gainXp(l: OwnedLippie, amount: number): Growth {
  const out: Growth = { lines: [], leveled: false, evolvedTo: null };
  if (l.level >= LEVEL_CAP) return out;
  const name = l.nickname || formName(l.form);
  out.lines.push(`${name} gained ${amount} XP!`);
  l.xp += amount;
  while (l.level < LEVEL_CAP && l.xp >= xpToNext(l.level)) {
    l.xp -= xpToNext(l.level);
    const before = statsFor(l.form, l.level).maxHp;
    l.level++;
    const after = statsFor(l.form, l.level).maxHp;
    l.hp = Math.min(after, l.hp + (after - before));
    out.leveled = true;
    out.lines.push(`${name} grew to level ${l.level}!`);
  }
  if (l.level >= LEVEL_CAP) l.xp = 0;
  const sp = speciesOf(l.form);
  if (sp.evolved && l.form === sp.id && l.level >= sp.evolved.level) {
    const oldName = name;
    const oldMax = statsFor(l.form, l.level).maxHp;
    l.form = sp.evolved.id;
    const newMax = statsFor(l.form, l.level).maxHp;
    l.hp = Math.min(newMax, l.hp + (newMax - oldMax));
    if (!l.nickname || l.nickname === sp.name) l.nickname = sp.evolved.name;
    out.evolvedTo = sp.evolved.id;
    out.lines.push(`What? ${oldName} is evolving!`, `${oldName} evolved into ${sp.evolved.name}!`);
  }
  return out;
}

export function ownedMaxHp(l: OwnedLippie) {
  return statsFor(l.form, l.level).maxHp;
}

export function newOwned(form: string, level: number): OwnedLippie {
  return { species: speciesOf(form).id, form, nickname: formName(form), level, xp: 0, hp: statsFor(form, level).maxHp };
}
